import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, debounceTime, distinctUntilChanged, map, Observable, Subscription, take } from 'rxjs';
import { FormControl } from '@angular/forms';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/app/config/global.config';
import { AppState } from 'src/app/core/app.state';
import { IPageCount, IPagination } from 'src/app/shared/models/page.model';
import { DeleteCompaniesGroups, DeleteCompaniesGroupsSuccess, FetchAllCompaniesGroups, SetGroupFilter } from '../../store/companies-groups.actions';
import { ICompaniesGroups, IGroupFilter, IGroupFilterRequest } from '../../models/companies-groups.model';
import { ActivatedRoute, Router } from '@angular/router';
import { SetCurrentElementId } from 'src/app/shared/store/shared.actions';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { MatDialog } from '@angular/material/dialog';
import { encodeFilterObject, parseFilterRequest, refillFilter } from 'src/app/shared/utils/helpers';
import { TranslateService } from '@ngx-translate/core';
import { Actions, ofType } from '@ngrx/effects';
import { DATE_TIME_FORMAT } from 'src/app/config/global.config';
import { FilterGroupComponent } from '../filter-group/filter-group.component';
import { SelectionController } from 'src/app/shared/utils/selection-controller';


@Component({
  selector: 'app-list-companies-groups',
  templateUrl: './list-companies-groups.component.html',
  styleUrls: ['./list-companies-groups.component.scss']
})
export class ListCompaniesGroupsComponent implements OnInit, OnDestroy {
  companiesGroups$: Observable<ICompaniesGroups[] | null> = this.store.select(state => state.companiesGroups.companiesGroups);
  pagination$: Observable<IPagination | null> = this.store.select((state) => state.companiesGroups.metadata);
  companiesGroupsList: ICompaniesGroups[] = [];
  filter: IGroupFilterRequest | undefined = undefined;
  key: string | undefined = undefined;
  private subscriptions: Subscription[] = [];
  page: IPageCount = {
    size: DEFAULT_PAGE_SIZE,
    number: 1
  };
  isAppLoading$ = this.store.select((state) => state.shared.isAppLoading);
  unparsedFilter: IGroupFilter | null = null;
  module: string = 'companyGroup';

  readonly canCreate$ = this.store.select(selectCanDo('company', 'create'));
  readonly canEdit$ = this.store.select(selectCanDo('company', 'edit'));
  readonly canDelete$ = this.store.select(selectCanDo('company', 'delete'));
  readonly canEditOrDelete$ = combineLatest([this.canEdit$, this.canDelete$]).pipe(
    map(([canEdit, canDelete]) => canEdit || canDelete)
  );

  DATE_TIME_FORMAT = DATE_TIME_FORMAT;

  searchControl = new FormControl('');
  /** Row selection, range gestures and the tri-state header, in one object. */
  readonly selection = new SelectionController<ICompaniesGroups>((group) => group.id);

  constructor(
    private store: Store<AppState>,
    private router: Router,
    public dialog: MatDialog,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private actions$: Actions
  ) {
    this.activatedRoute.queryParams.subscribe(params => {
      const pageNumber = parseInt(params['page'] || '1');
      const pageCount = PAGE_SIZE_OPTIONS.includes(parseInt(params['count'])) ? parseInt(params['count']) : DEFAULT_PAGE_SIZE;
      this.key = params['keyword'] ?? '';
      if (params['filter']) {
        try {
          this.unparsedFilter = refillFilter(this.module, JSON.parse(decodeURIComponent(params['filter']))) as IGroupFilter;
          if (this.unparsedFilter)
            this.filter = parseFilterRequest(this.module, this.unparsedFilter);
        } catch (error) {
          this.filter == undefined;
        }
      }
      this.page = { ...this.page, number: pageNumber, size: pageCount };
    });
  }

  ngOnInit(): void {
    this.searchControl.setValue(this.key || '', { emitEvent: false });

    const sub1 = this.store.select((state) => state.companiesGroups.groupFilterState)
      .pipe(
        debounceTime(10),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      )
      .subscribe((filter) => {
        this.unparsedFilter = filter.advanced;
        this.filter = parseFilterRequest(this.module, filter.advanced);
        this.key = filter.keyword;
        this.searchControl.setValue(this.key || '', { emitEvent: false });
        this.toPage(this.page);
      });

    const sub2 = this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(value => {
        const next = (value ?? '').toString();
        if (next === (this.key ?? '')) return;
        this.page = { ...this.page, number: 1 };
        this.store.dispatch(SetGroupFilter({ keyword: next, advanced: this.unparsedFilter }));
      });

    // Deferred so the selection state settles outside the change-detection pass
    // that delivered the new rows, which would otherwise trip ExpressionChanged.
    const sub3 = this.companiesGroups$.subscribe(groups => {
      Promise.resolve().then(() => this.selection.setRows(groups));
    });

    this.subscriptions.push(sub1, sub2, sub3);
  }

  onRowClick(id: number): void {
    this.eidtCompaniesGroupe(id);
  }

  toPage(page: IPageCount) {
    this.page = { ...this.page, number: page.number, size: page.size };
    this.store.dispatch(FetchAllCompaniesGroups({
      page: this.page,
      filter: this.filter,
      key: this.key
    }));
    let filter: any = {};
    if (this.key && this.key != '') {
      filter.keyword = this.key;
    }
    filter.filter = encodeFilterObject(this.unparsedFilter);
    let queryParams = { page: this.page.number, count: this.page.size, ...filter };
    this.router.navigate([], {
      queryParams: queryParams
    });
  }

  addCompaniesGroups() {
    this.router.navigate(['add'], {
      relativeTo: this.activatedRoute,
      queryParamsHandling: 'merge',
    });
  }

  eidtCompaniesGroupe(id: number) {
    let filter: any = {};
    if (this.key && this.key != '') {
      filter.keyword = this.key;
    }
    filter.filter = encodeFilterObject(this.unparsedFilter);
    let queryParams = { page: this.page.number, count: this.page.size, ...filter };
    this.router.navigate(['companies-groups/', id], {
      queryParams: queryParams,
    });
    this.store.dispatch(SetCurrentElementId({ currentElementId: id }));
  }

  // ── Filter dialog ───────────────────────────────────────────────────────

  openFilter() {
    const dialogRef = this.dialog.open(FilterGroupComponent, {
      width: '900px',
    });
    dialogRef.afterClosed().subscribe((filterResult: any) => {
      if (filterResult) {
        this.page = { ...this.page, number: 1 };
        this.store.dispatch(SetGroupFilter({ keyword: this.key ?? '', advanced: filterResult }));
      }
    });
  }

  hasFilters(): boolean {
    return !!(this.filter && this.filter.filters && this.filter.filters.length > 0);
  }

  clearFilters() {
    this.page = { ...this.page, number: 1 };
    const queryParams: any = { page: this.page.number, count: this.page.size, filter: null };
    if (this.key && this.key !== '') {
      queryParams.keyword = this.key;
    }
    this.router.navigate([], { queryParams, queryParamsHandling: 'merge' });
    this.unparsedFilter = null;
    const cleared = refillFilter(this.module, null) as IGroupFilter;
    this.store.dispatch(SetGroupFilter({ keyword: this.key ?? '', advanced: cleared }));
  }

  // ── Selection ───────────────────────────────────────────────────────────

  bulkDelete(): void {
    if (!this.selection.hasSelection) return;
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (!result) return;
      const ids = this.selection.ids;
      this.selection.clear();
      const refreshSub = this.actions$.pipe(
        ofType(DeleteCompaniesGroupsSuccess),
        take(ids.length)
      ).subscribe(() => {
        refreshSub.unsubscribe();
        this.toPage(this.page);
      });
      ids.forEach(id => this.store.dispatch(DeleteCompaniesGroups({ id })));
    });
  }

  delete(companyGroup: number) {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        const deleteSuccessSub = this.actions$.pipe(
          ofType(DeleteCompaniesGroupsSuccess),
          take(1)
        ).subscribe(() => {
          this.toPage(this.page);
          deleteSuccessSub.unsubscribe();
        });

        this.store.dispatch(DeleteCompaniesGroups({ id: companyGroup }));
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
